/**
 * La mappa delle aziende nella console (`/tenants/mappa`, 27/10/2026): tutte le aziende sulla carta d'Italia, coi
 * filtri per stato e livello, la scheda di ognuna e i link per mostrarla a chi non ha un accesso.
 *
 * La pagina occupa tutto lo spazio del contenuto (la carta è la pagina): esce dai margini della console con margini
 * negativi uguali a quelli di `ConsoleLayout`.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, CircularProgress, IconButton, Tooltip, Typography, useMediaQuery, useTheme } from '@mui/material';
import { IosShare, Refresh } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { RispostaMappaConsole } from '@shared/admin/mappaAziende';
import { tokens } from '../theme';
import { leggiMappaAziende, motivoErrore } from '../services/mappaAziendeService';
import { COLORI_MAPPA } from '../components/mappa/aspetto';
import { CondividiMappa } from '../components/mappa/CondividiMappa';
import { useAggiornamentoPeriodico } from '../components/mappa/aggiornamento';
import { useFormatiMappa } from '../components/mappa/formati';
import { InterruttoreVista } from '../components/mappa/InterruttoreVista';
import { VistaMappaAziende } from '../components/mappa/VistaMappaAziende';

export const MappaAziendePage: React.FC = () => {
  const { t } = useTranslation('mappa');
  const navigate = useNavigate();
  const theme = useTheme();
  const telefono = useMediaQuery(theme.breakpoints.down('sm'));
  const formati = useFormatiMappa();
  const [dati, setDati] = useState<RispostaMappaConsole | null>(null);
  const [caricando, setCaricando] = useState(true);
  const [errore, setErrore] = useState<string | null>(null);
  const [condividi, setCondividi] = useState(false);

  const carica = useCallback(async () => {
    setCaricando(true);
    setErrore(null);
    try {
      setDati(await leggiMappaAziende());
    } catch (e) {
      console.error('[MappaAziendePage] lettura della mappa non riuscita', e);
      setErrore(motivoErrore(e));
    } finally {
      setCaricando(false);
    }
  }, []);

  useEffect(() => { void carica(); }, [carica]);

  // Ogni minuto, in silenzio: un errore qui lascia i dati di prima e si scrive nella console del browser.
  const aggiornaInSilenzio = useCallback(async () => {
    try {
      setDati(await leggiMappaAziende());
    } catch (e) {
      console.warn('[MappaAziendePage] aggiornamento automatico non riuscito', e);
    }
  }, []);
  useAggiornamentoPeriodico(aggiornaInSilenzio, !!dati);

  const intestazione = (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 1.25 }}>
      <InterruttoreVista attiva="mappa" />
      <Box>
        <Typography
          component="h1"
          sx={{
            fontFamily: tokens.font.display,
            fontWeight: 600,
            fontSize: { xs: '1.45rem', md: '1.95rem' },
            lineHeight: 1.1,
            letterSpacing: '-0.02em',
            color: tokens.color.graphite,
            fontVariationSettings: '"WONK" 0, "SOFT" 30',
            // Sul telefono l'interruttore «Mappa» dice già dove si è: il titolo lascia il posto alla carta.
            '@media (max-width: 599.95px)': { position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap' },
          }}
        >
          {t('pagina.titolo')}
        </Typography>
      </Box>
    </Box>
  );

  const azioni = (
    <>
      <Tooltip title={t('pagina.aggiorna')}>
        <span>
          <IconButton onClick={() => void carica()} disabled={caricando} aria-label={t('pagina.aggiorna')} sx={{ width: 44, height: 44, bgcolor: tokens.color.paper, border: `1px solid ${tokens.color.line}`, '&:hover': { bgcolor: tokens.color.paper } }}>
            {caricando ? <CircularProgress size={18} /> : <Refresh fontSize="small" />}
          </IconButton>
        </span>
      </Tooltip>
      {telefono ? (
        <Tooltip title={t('pagina.condividi')}>
          <IconButton onClick={() => setCondividi(true)} disabled={!dati} aria-label={t('pagina.condividi')} sx={{ width: 44, height: 44, bgcolor: tokens.color.graphite, color: tokens.color.paper, '&:hover': { bgcolor: '#000' } }}>
            <IosShare fontSize="small" />
          </IconButton>
        </Tooltip>
      ) : (
        <Button
          onClick={() => setCondividi(true)}
          disabled={!dati}
          variant="contained"
          disableElevation
          startIcon={<IosShare fontSize="small" />}
          sx={{ minHeight: 44, px: 2.25, bgcolor: tokens.color.graphite, '&:hover': { bgcolor: '#000' }, textTransform: 'none', fontWeight: 600, borderRadius: `${tokens.radius.pill}px` }}
        >
          {t('pagina.condividi')}
        </Button>
      )}
    </>
  );

  return (
    <Box
      sx={{
        // La console mette attorno al contenuto 12/16/24 px: la carta li riprende e arriva ai bordi.
        m: { xs: -1.5, sm: -2, md: -3 },
        height: { xs: 'calc(100dvh - 56px)', sm: 'calc(100dvh - 64px)' },
        position: 'relative',
        bgcolor: COLORI_MAPPA.mare,
      }}
    >
      {errore && !dati ? (
        <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'flex-start' }}>
          <InterruttoreVista attiva="mappa" />
          <Alert severity="error" action={<Button color="inherit" onClick={() => void carica()}>{t('pagina.riprova')}</Button>}>
            {t('pagina.errore', { motivo: errore })}
          </Alert>
        </Box>
      ) : !dati ? (
        <Box sx={{ height: '100%', display: 'grid', placeItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, color: tokens.color.ash }}>
            <CircularProgress size={20} />
            <Typography sx={{ fontSize: '0.9rem' }}>{t('pagina.caricamento')}</Typography>
          </Box>
        </Box>
      ) : (
        <>
          {errore && <Alert severity="error" sx={{ position: 'absolute', zIndex: 3, left: 16, right: 16, bottom: 16 }} onClose={() => setErrore(null)}>{t('pagina.errore', { motivo: errore })}</Alert>}
          <VistaMappaAziende
            modo="console"
            aziende={dati.aziende}
            giorniPersoneAttive={dati.giorni_persone_attive}
            minutiAdesso={dati.minuti_adesso}
            intestazione={intestazione}
            azioni={azioni}
            piede={
              <>
                <Typography>{t('pagina.aggiornata', { quando: formati.dataOra(dati.calcolata_il) })}</Typography>
                <Typography>{t('pagina.fonte_confini')}</Typography>
              </>
            }
            onApriAzienda={(id) => navigate(`/tenants/${id}`)}
          />
          <CondividiMappa
            aperta={condividi}
            onChiudi={() => setCondividi(false)}
            condivisioni={dati.condivisioni}
            onCondivisioni={(condivisioni) => setDati((d) => (d ? { ...d, condivisioni } : d))}
            formati={formati}
          />
        </>
      )}
    </Box>
  );
};
